import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import { FraudRule, ApiResponse } from '../types';
import clsx from 'clsx';

const ruleTypeLabels: Record<string, string> = {
  ip_duplicate: 'IP Duplicate Detection',
  device_fingerprint: 'Device Fingerprint',
  velocity: 'Velocity Check',
  geo_mismatch: 'Geo Mismatch',
  bot_detection: 'Bot Detection',
  time_pattern: 'Time Pattern Analysis',
  amount_threshold: 'Amount Threshold',
  custom: 'Custom Rule',
};

const actionColors: Record<string, string> = {
  none: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
  flag: 'bg-yellow-100 text-yellow-800',
  hold: 'bg-orange-100 text-orange-800',
  reject: 'bg-red-100 text-red-800',
};

export default function FraudRules() {
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<FraudRule | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['fraud-rules'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<FraudRule[]>>('/fraud/rules');
      return response.data;
    },
  });

  const createRule = useMutation({
    mutationFn: async (data: Partial<FraudRule>) => {
      await api.post('/fraud/rules', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fraud-rules'] });
      toast.success('Fraud rule created');
      setShowModal(false);
    },
    onError: () => {
      toast.error('Failed to create rule');
    },
  });

  const updateRule = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<FraudRule> }) => {
      await api.patch(`/fraud/rules/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fraud-rules'] });
      toast.success('Fraud rule updated');
      setShowModal(false);
      setEditingRule(null);
    },
  });

  const deleteRule = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/fraud/rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fraud-rules'] });
      toast.success('Fraud rule deleted');
    },
  });

  const toggleStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await api.patch(`/fraud/rules/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fraud-rules'] });
      toast.success('Rule status updated');
    },
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Fraud Detection Rules</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Configure fraud detection rules to protect your campaigns from fraudulent activity.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            onClick={() => {
              setEditingRule(null);
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            Add Rule
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Rules"
          value={data?.data?.length || 0}
          icon="shield"
        />
        <SummaryCard
          title="Active Rules"
          value={data?.data?.filter((r) => r.status === 'active').length || 0}
          icon="check"
          color="green"
        />
        <SummaryCard
          title="Flagging Rules"
          value={data?.data?.filter((r) => r.action === 'flag').length || 0}
          icon="flag"
          color="yellow"
        />
        <SummaryCard
          title="Rejection Rules"
          value={data?.data?.filter((r) => r.action === 'reject').length || 0}
          icon="x"
          color="red"
        />
      </div>

      {/* Rules Table */}
      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">
                Rule
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Type
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Applies To
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Score Impact
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Action
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Status
              </th>
              <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-[var(--text-muted)]">
                  Loading...
                </td>
              </tr>
            ) : data?.data?.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-[var(--text-muted)]">
                  No fraud rules configured
                </td>
              </tr>
            ) : (
              data?.data?.map((rule) => (
                <tr key={rule.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                    <div className="font-medium text-[var(--text-primary)]">{rule.name}</div>
                    {rule.description && (
                      <div className="text-[var(--text-muted)] text-xs truncate max-w-xs">
                        {rule.description}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {ruleTypeLabels[rule.ruleType] || rule.ruleType}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    <span className="capitalize">{rule.appliesTo}</span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span className={clsx(
                      'font-medium',
                      rule.scoreImpact >= 30 ? 'text-red-600' :
                      rule.scoreImpact >= 15 ? 'text-yellow-600' : 'text-[var(--text-secondary)]'
                    )}>
                      +{rule.scoreImpact}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span className={clsx('badge', actionColors[rule.action])}>
                      {rule.action}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <button
                      onClick={() => toggleStatus.mutate({
                        id: rule.id,
                        status: rule.status === 'active' ? 'inactive' : 'active'
                      })}
                      className={clsx(
                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                        rule.status === 'active' ? 'bg-primary-600' : 'bg-gray-200'
                      )}
                    >
                      <span
                        className={clsx(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-[var(--bg-secondary)] shadow ring-0 transition duration-200 ease-in-out',
                          rule.status === 'active' ? 'translate-x-5' : 'translate-x-0'
                        )}
                      />
                    </button>
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <button
                      onClick={() => {
                        setEditingRule(rule);
                        setShowModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this rule?')) {
                          deleteRule.mutate(rule.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <FraudRuleModal
          rule={editingRule}
          onClose={() => {
            setShowModal(false);
            setEditingRule(null);
          }}
          onSave={(data) => {
            if (editingRule) {
              updateRule.mutate({ id: editingRule.id, data });
            } else {
              createRule.mutate(data);
            }
          }}
          isLoading={createRule.isPending || updateRule.isPending}
        />
      )}
    </div>
  );
}

function SummaryCard({
  title,
  value,
  icon,
  color = 'gray',
}: {
  title: string;
  value: number;
  icon: string;
  color?: string;
}) {
  const colorClasses: Record<string, string> = {
    gray: 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
  };

  return (
    <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-4 flex items-center gap-4">
      <div className={clsx('p-3 rounded-full', colorClasses[color])}>
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {icon === 'shield' && (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          )}
          {icon === 'check' && (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          )}
          {icon === 'flag' && (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
          )}
          {icon === 'x' && (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          )}
        </svg>
      </div>
      <div>
        <p className="text-sm text-[var(--text-muted)]">{title}</p>
        <p className="text-2xl font-semibold text-[var(--text-primary)]">{value}</p>
      </div>
    </div>
  );
}

function FraudRuleModal({
  rule,
  onClose,
  onSave,
  isLoading,
}: {
  rule: FraudRule | null;
  onClose: () => void;
  onSave: (data: Partial<FraudRule>) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    ruleType: rule?.ruleType || 'ip_duplicate',
    appliesTo: rule?.appliesTo || 'both',
    scoreImpact: rule?.scoreImpact || 10,
    action: rule?.action || 'flag',
    parameters: rule?.parameters || {},
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-[var(--bg-secondary)]0 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-[var(--bg-secondary)] rounded-lg shadow-xl p-6 max-w-lg w-full">
          <h3 className="text-lg font-medium mb-4">
            {rule ? 'Edit Fraud Rule' : 'Create Fraud Rule'}
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSave(formData);
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Rule Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input mt-1"
                placeholder="e.g., Duplicate IP Check"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input mt-1"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Rule Type</label>
                <select
                  value={formData.ruleType}
                  onChange={(e) => setFormData({ ...formData, ruleType: e.target.value })}
                  className="input mt-1"
                >
                  <option value="ip_duplicate">IP Duplicate Detection</option>
                  <option value="device_fingerprint">Device Fingerprint</option>
                  <option value="velocity">Velocity Check</option>
                  <option value="geo_mismatch">Geo Mismatch</option>
                  <option value="bot_detection">Bot Detection</option>
                  <option value="time_pattern">Time Pattern Analysis</option>
                  <option value="amount_threshold">Amount Threshold</option>
                  <option value="custom">Custom Rule</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Applies To</label>
                <select
                  value={formData.appliesTo}
                  onChange={(e) => setFormData({ ...formData, appliesTo: e.target.value as any })}
                  className="input mt-1"
                >
                  <option value="clicks">Clicks Only</option>
                  <option value="conversions">Conversions Only</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Score Impact (+)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.scoreImpact}
                  onChange={(e) => setFormData({ ...formData, scoreImpact: parseInt(e.target.value) })}
                  className="input mt-1"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Points added to fraud score when triggered
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Action</label>
                <select
                  value={formData.action}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value as any })}
                  className="input mt-1"
                >
                  <option value="none">None (Score Only)</option>
                  <option value="flag">Flag for Review</option>
                  <option value="hold">Hold for Approval</option>
                  <option value="reject">Auto Reject</option>
                </select>
              </div>
            </div>

            {/* Parameters based on rule type */}
            {formData.ruleType === 'velocity' && (
              <div className="bg-[var(--bg-secondary)] p-4 rounded-lg">
                <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-3">Velocity Parameters</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[var(--text-muted)]">Max Events</label>
                    <input
                      type="number"
                      value={formData.parameters.maxEvents || 10}
                      onChange={(e) => setFormData({
                        ...formData,
                        parameters: { ...formData.parameters, maxEvents: parseInt(e.target.value) }
                      })}
                      className="input mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-muted)]">Time Window (minutes)</label>
                    <input
                      type="number"
                      value={formData.parameters.timeWindow || 60}
                      onChange={(e) => setFormData({
                        ...formData,
                        parameters: { ...formData.parameters, timeWindow: parseInt(e.target.value) }
                      })}
                      className="input mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.ruleType === 'amount_threshold' && (
              <div className="bg-[var(--bg-secondary)] p-4 rounded-lg">
                <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-3">Amount Threshold</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[var(--text-muted)]">Min Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.parameters.minAmount || 0}
                      onChange={(e) => setFormData({
                        ...formData,
                        parameters: { ...formData.parameters, minAmount: parseFloat(e.target.value) }
                      })}
                      className="input mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-muted)]">Max Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.parameters.maxAmount || 10000}
                      onChange={(e) => setFormData({
                        ...formData,
                        parameters: { ...formData.parameters, maxAmount: parseFloat(e.target.value) }
                      })}
                      className="input mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="btn btn-primary">
                {isLoading ? 'Saving...' : rule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
